import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import studioApplicationService from '../../utils/studioApplicationService';

const StudioApplicationPending = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadApplication = async () => {
      if (!user) {
        navigate('/user-login');
        return;
      }

      const result = await studioApplicationService.getMyApplication();

      if (result.success) {
        if (!result.data) {
          // No application found, redirect to application form
          navigate('/studio-application-form');
        } else {
          setApplication(result.data);
        }
      }

      setLoading(false);
    };

    loadApplication();
  }, [user, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Icon name="Loader2" size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  const getStatusInfo = () => {
    switch (application?.status) {
      case 'pending':
        return {
          icon: 'Clock',
          iconColor: 'text-blue-500',
          bgColor: 'bg-blue-500/10',
          borderColor: 'border-blue-500/30',
          title: 'Application Under Review',
          description: 'Thank you for applying! Our team is reviewing your studio application.',
          message: 'We typically review applications within 2-3 business days. You\'ll receive an email once your application has been reviewed.'
        };
      case 'approved':
        return {
          icon: 'CheckCircle',
          iconColor: 'text-green-500',
          bgColor: 'bg-green-500/10',
          borderColor: 'border-green-500/30',
          title: 'Application Approved!',
          description: 'Congratulations! Your studio has been approved.',
          message: 'You can now access your studio dashboard and start publishing games.'
        };
      case 'rejected':
        return {
          icon: 'XCircle',
          iconColor: 'text-red-500',
          bgColor: 'bg-red-500/10',
          borderColor: 'border-red-500/30',
          title: 'Application Not Approved',
          description: 'Unfortunately, we couldn\'t approve your studio application at this time.',
          message: application.rejection_reason || 'If you believe this was a mistake, please contact our support team.'
        };
      default:
        return null;
    }
  };

  const statusInfo = getStatusInfo();

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <div className={`bg-card border ${statusInfo.borderColor} rounded-2xl shadow-gaming-xl p-8`}>
          {/* Icon */}
          <div className={`w-16 h-16 ${statusInfo.bgColor} rounded-full flex items-center justify-center mx-auto mb-6`}>
            <Icon name={statusInfo.icon} size={32} className={statusInfo.iconColor} />
          </div>

          {/* Title */}
          <h1 className="text-3xl font-bold text-foreground text-center mb-2">
            {statusInfo.title}
          </h1>

          {/* Description */}
          <p className="text-lg text-muted-foreground text-center mb-6">
            {statusInfo.description}
          </p>

          {/* Application Details */}
          <div className="bg-muted/30 rounded-lg p-6 mb-6">
            <h3 className="text-sm font-medium text-foreground mb-4">Application Details</h3>
            <dl className="space-y-3">
              <div>
                <dt className="text-xs text-muted-foreground">Studio Name</dt>
                <dd className="text-sm font-medium text-foreground">{application?.studio_name}</dd>
              </div>
              {application?.studio_website && (
                <div>
                  <dt className="text-xs text-muted-foreground">Website</dt>
                  <dd className="text-sm text-primary">
                    <a href={application.studio_website} target="_blank" rel="noopener noreferrer" className="hover:underline">
                      {application.studio_website}
                    </a>
                  </dd>
                </div>
              )}
              <div>
                <dt className="text-xs text-muted-foreground">Team Size</dt>
                <dd className="text-sm font-medium text-foreground capitalize">{application?.team_size?.replace('-', ' to ')}</dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">Submitted</dt>
                <dd className="text-sm font-medium text-foreground">
                  {new Date(application?.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </dd>
              </div>
            </dl>
          </div>

          {/* Message */}
          <div className={`${statusInfo.bgColor} border ${statusInfo.borderColor} rounded-lg p-4 mb-6`}>
            <p className="text-sm text-foreground">
              {statusInfo.message}
            </p>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            {application?.status === 'approved' && (
              <Button
                variant="default"
                size="lg"
                fullWidth
                onClick={() => navigate('/studio-dashboard')}
                iconName="ArrowRight"
              >
                Go to Studio Dashboard
              </Button>
            )}

            <Button
              variant={application?.status === 'approved' ? 'outline' : 'default'}
              size="lg"
              fullWidth
              onClick={() => navigate('/game-library-dashboard')}
            >
              Go to Dashboard
            </Button>

            {application?.status === 'rejected' && (
              <Button
                variant="outline"
                size="lg"
                fullWidth
                onClick={() => navigate('/contact-us')}
                iconName="MessageCircle"
              >
                Contact Support
              </Button>
            )}
          </div>

          {/* Help text */}
          <p className="text-xs text-muted-foreground text-center mt-6">
            Need help? <a href="/contact-us" className="text-primary hover:underline">Contact our support team</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default StudioApplicationPending;
